import {
  Color,
  EventDetailsForView,
  EventWithCustomField,
  ParticipationRequestType,
} from "@ac-expo/api-types";
import { formatRGBColor, isEventInPast } from "@ac-expo/utils";
import { noop } from "lodash";
import { ReactNode } from "react";
import { cn } from "../../../utils";
import { EventViewHeaderCard } from "./EventViewHeaderCard";
import { EventViewPictureCard } from "./EventViewPictureCard";
import { EventPanel, EventViewPanelsCard } from "./EventViewTabs";
import { EventViewTicketingCard, TicketTypeItem } from "./EventViewTicketingCard";

type EventModelViewProps = {
  event: EventWithCustomField<EventDetailsForView>;
  pictureRender: () => ReactNode;
  descriptionRender: () => ReactNode;
  ticketRender: () => ReactNode;
  informationRender: () => ReactNode;
  backgroundPictureUrl: string | undefined;
  backgroundColor?: Color;
  opacity?: number;
  model: number;
};

type EventBackgroundProps = {
  url: string;
  opacity?: number;
  rounded?: boolean;
};

// Blurred copy fills the whole (tab-driven) height; the sharp copy keeps a
// stable top-anchored crop so it does not shift when the active tab changes.
const EventBackground = ({ url, opacity, rounded }: EventBackgroundProps) => {
  const style = { opacity: (opacity ?? 100) / 100 };
  return (
    <>
      <img
        src={url}
        alt=""
        aria-hidden="true"
        className={cn(
          "absolute inset-0 z-0 h-full w-full scale-110 object-cover blur-2xl",
          rounded && "rounded-t-[9px]",
        )}
        style={style}
      />
      <img
        src={url}
        alt=""
        aria-hidden="true"
        className={cn("absolute left-0 top-0 z-10 h-auto w-full", rounded && "rounded-t-[9px]")}
        style={style}
      />
    </>
  );
};

export function EventModelView({
  event,
  pictureRender,
  descriptionRender,
  ticketRender,
  informationRender,
  backgroundPictureUrl,
  backgroundColor,
  opacity,
  model,
}: EventModelViewProps) {
  const currentBackgroundColor = formatRGBColor(backgroundColor ?? "white");

  return (
    <>
      <div
        style={{
          backgroundColor: currentBackgroundColor,
        }}
        className="relative overflow-hidden lg:hidden"
      >
        {backgroundPictureUrl && (
          <EventBackground url={backgroundPictureUrl} opacity={opacity} />
        )}
        <div className="relative flex flex-col gap-4 md:p-6">
          <div className="z-20 flex w-full items-center justify-center border-none aspect-[1280/486]">
            {pictureRender()}
          </div>
          <div className="z-20 flex flex-col gap-2">
            <div className="flex items-center justify-center border-none">
              {descriptionRender()}
            </div>
            <div className="border-nonep-4 flex w-full max-w-full items-center justify-center">
              {informationRender()}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          backgroundColor: currentBackgroundColor,
        }}
        className="relative hidden overflow-hidden lg:block"
      >
        {backgroundPictureUrl && (
          <EventBackground url={backgroundPictureUrl} opacity={opacity} rounded />
        )}
        {model === 0 && (
          <div className="relative flex flex-col gap-4 md:p-6">
            <div className="z-20 flex w-full items-center justify-center border-none aspect-[1280/486]">
              {pictureRender()}
            </div>
            <div className="z-20 flex flex-row gap-2">
              <div className="flex w-2/3 max-w-full flex-col gap-2">
                <div className="flex items-center justify-center border-none">
                  {descriptionRender()}
                </div>
                <div className="border-nonep-4 flex w-full max-w-full items-center justify-center">
                  {informationRender()}
                </div>
              </div>
              <div className="flex h-fit w-1/3 items-center justify-center border-none">
                {ticketRender()}
              </div>
            </div>
          </div>
        )}
        {model === 1 && (
          <div className="relative flex flex-col gap-4 p-6">
            <div className="z-20 flex grow items-center justify-center border-none">
              {descriptionRender()}
            </div>
            <div className="z-20 flex w-full items-center justify-center border-none">
              {pictureRender()}
            </div>
            <div className="z-20 flex flex-row gap-2">
              <div className="flex w-2/3 flex-col gap-2">
                <div className="flex items-center justify-center border-none p-4">
                  {informationRender()}
                </div>
              </div>
              <div className="z-20 flex h-fit w-1/3 items-center justify-center border-none">
                {ticketRender()}
              </div>
            </div>
          </div>
        )}
        {model === 2 && (
          <div className="relative flex flex-row gap-4 p-6">
            <div className="z-20 flex w-2/3 flex-col gap-2">
              <div className="flex w-full items-center justify-center border-none">
                {pictureRender()}
              </div>
              <div className="flex w-full items-center justify-center border-none">
                {informationRender()}
              </div>
            </div>
            <div className="z-20 flex h-3/5 w-1/3 flex-col gap-2">
              <div className="flex w-full items-center justify-center border-none">
                {descriptionRender()}
              </div>
              <div className="flex w-full items-center justify-center border-none">
                {ticketRender()}
              </div>
            </div>
          </div>
        )}
        {model === 3 && (
          <div className="relative flex flex-row gap-4 p-6">
            <div className="z-20 flex h-4/5 w-2/3 flex-col gap-2">
              <div className="flex w-full items-center justify-center border-none">
                {descriptionRender()}
              </div>
              <div className="flex w-full items-center justify-center border-none">
                {informationRender()}
              </div>
            </div>
            <div className="z-20 flex w-1/3 flex-col gap-2">
              <div className="flex w-full items-center justify-center border-none">
                {pictureRender()}
              </div>
              <div className="flex w-full items-center justify-center border-none">
                {ticketRender()}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

type EventViewProps = {
  as: "organiser" | "exhibitor" | "visitor";
  thumbnailImageUrl: string;
  backgroundImageUrl: string;
  model: number;
  opacity?: number;
  backgroundColor?: Color;
  event: EventWithCustomField<EventDetailsForView>;
  ticketTypes: TicketTypeItem[];
  activityTicketTypes: TicketTypeItem[];
  badgeTypes: TicketTypeItem[];
  participationRequestStatus: ParticipationRequestType["status"] | null;
  isTicketingVisible: boolean;
  navigateBack: () => void;
  openTicketing?: () => void;
  /** Group unlock line shown under the public prices of the ticketing card. */
  groupUnlockLine?: ReactNode;
  sendParticipationRequest?: () => void;
  panels: Record<EventPanel, ReactNode>;
  onPanelChange?: (to: EventPanel) => void;
  activePanel?: EventPanel;
  className?: string;
  getWebAppUrl: () => string;
  toggleFavoriteButton: ReactNode;
};

export function EventView({
  as,
  participationRequestStatus,
  event,
  ticketTypes,
  activityTicketTypes,
  badgeTypes,
  thumbnailImageUrl,
  backgroundImageUrl,
  isTicketingVisible,
  navigateBack,
  openTicketing,
  groupUnlockLine,
  sendParticipationRequest,
  panels,
  model,
  opacity,
  backgroundColor,
  getWebAppUrl,
  toggleFavoriteButton = null,
  onPanelChange,
  activePanel,
}: EventViewProps) {
  const onOpenTicketing = openTicketing ?? noop;
  const onSendParticipationRequest = sendParticipationRequest ?? noop;

  return (
    <EventModelView
      event={event}
      descriptionRender={() => (
        <EventViewHeaderCard
          event={event}
          getWebAppUrl={getWebAppUrl}
          toggleFavoriteButton={toggleFavoriteButton}
          as={as}
          isEventInThePast={isEventInPast(
            event.dates ?? {
              schedules: [],
              exceptionalClosingDates: [],
            },
          )}
          sendParticipationRequest={onSendParticipationRequest}
          eventAdminId={event.organiser.adminId}
          participationRequestStatus={participationRequestStatus}
        />
      )}
      informationRender={() => (
        <EventViewPanelsCard
          event={event}
          panels={panels}
          activePanel={activePanel}
          onPanelChange={onPanelChange}
        />
      )}
      ticketRender={() => (
        <EventViewTicketingCard
          ticketTypes={ticketTypes}
          activityTicketTypes={activityTicketTypes}
          badgeTypes={badgeTypes}
          isTicketingVisible={isTicketingVisible}
          openTicketing={onOpenTicketing}
          hasHtmlContent={true}
          groupUnlockLine={groupUnlockLine}
        />
      )}
      pictureRender={() => (
        <EventViewPictureCard
          thumbnailImageUrl={thumbnailImageUrl}
          navigateBack={navigateBack}
          schedules={event.dates?.schedules ?? []}
          exceptionalClosingDates={event.dates?.exceptionalClosingDates ?? []}
          isCountdownVisible={event.customEventAppearance?.countdown ?? false}
        />
      )}
      backgroundPictureUrl={backgroundImageUrl}
      model={model}
      opacity={opacity}
      backgroundColor={backgroundColor}
    />
  );
}
